import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

// Import styles from separate files
import globalStyles from './nuvance-decoupled-search-typeahead-results.global.pcss?inline';
import innerStyles from './nuvance-decoupled-search-typeahead-results.inner.pcss?inline';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';

/**
 * @element nuvance-decoupled-search-typeahead-results
 *
 * A component that displays typeahead search results from multiple endpoints.
 * This component provides slots for different types of results (Search Results, Locations, Doctors, Services, News)
 * and handles the display order and styling of these results.
 *
 * @slot search-results - Slot for general search results
 * @slot locations - Slot for location results
 * @slot doctors - Slot for doctor results
 * @slot services - Slot for service results
 * @slot news - Slot for news results
 *
 * @attr {boolean} visible - Whether the typeahead results are visible
 * @attr {boolean} has-search-results - Whether search results are available
 * @attr {boolean} has-locations - Whether location results are available
 * @attr {boolean} has-doctors - Whether doctor results are available
 * @attr {boolean} has-services - Whether service results are available
 * @attr {boolean} has-news - Whether news results are available
 * @attr {boolean} debug - Enable debug mode for additional logging
 */
@customElement('nuvance-decoupled-search-typeahead-results')
export class NuvanceDecoupledSearchTypeaheadResults extends LitElement {
  /**
   * AdoptedStyleSheets controller for managing component styles
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  /**
   * Bound event handler for outside clicks
   */
  private boundHandleOutsideClick: (event: MouseEvent) => void;

  /**
   * Constructor for the component
   */
  constructor() {
    super();
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
  }

  /**
   * Whether the typeahead results are visible
   */
  @property({ type: Boolean, reflect: true })
  visible = false;

  /**
   * Whether search results are available
   */
  @property({ type: Boolean, reflect: true, attribute: 'has-search-results' })
  hasSearchResults = false;

  /**
   * Whether location results are available
   */
  @property({ type: Boolean, reflect: true, attribute: 'has-locations' })
  hasLocations = false;

  /**
   * Whether doctor results are available
   */
  @property({ type: Boolean, reflect: true, attribute: 'has-doctors' })
  hasDoctors = false;

  /**
   * Whether service results are available
   */
  @property({ type: Boolean, reflect: true, attribute: 'has-services' })
  hasServices = false;

  /**
   * Whether news results are available
   */
  @property({ type: Boolean, reflect: true, attribute: 'has-news' })
  hasNews = false;

  /**
   * Debug mode flag
   */
  @property({ type: Boolean, attribute: 'debug' })
  debug = false;

  /**
   * Logs debug messages if debug mode is enabled
   */
  private debugLog(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(
        `[NuvanceDecoupledSearchTypeaheadResults] ${message}`,
        ...args
      );
    }
  }

  /**
   * Lifecycle callback when the component is connected to the DOM
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.debugLog('Component connected to DOM');

    // Add event listener for clicks outside the component to hide results
    document.addEventListener('click', this.boundHandleOutsideClick);
  }

  /**
   * Handles clicks outside the component to hide results
   */
  private handleOutsideClick(event: MouseEvent): void {
    if (this.visible && !this.contains(event.target as Node)) {
      this.hide();
    }
  }

  /**
   * Shows the typeahead results
   */
  show(): void {
    this.visible = true;
    this.debugLog('Showing typeahead results');
  }

  /**
   * Hides the typeahead results
   */
  hide(): void {
    this.visible = false;
    this.debugLog('Hiding typeahead results');
  }

  /**
   * Resets all section states
   */
  private resetSectionStates(): void {
    this.hasSearchResults = false;
    this.hasLocations = false;
    this.hasDoctors = false;
    this.hasServices = false;
    this.hasNews = false;
    this.requestUpdate();
  }

  /**
   * Lifecycle callback when the component is disconnected from the DOM
   */
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.debugLog('Component disconnected from DOM');

    // Remove event listener when component is disconnected
    document.removeEventListener('click', this.boundHandleOutsideClick);

    // Reset all section states
    this.resetSectionStates();
  }

  /**
   * Renders a section if it has results
   * @param hasResults - Whether the section has results to display
   * @param slotName - The name of the slot to render
   * @returns The rendered section or nothing
   */
  private renderSection(hasResults: boolean, slotName: string): unknown {
    if (!hasResults) {
      return nothing;
    }

    return html`
      <div class="typeahead-section ${slotName}-section">
        <slot name="${slotName}"></slot>
      </div>
    `;
  }

  /**
   * Renders the component
   */
  render() {
    this.debugLog('Rendering component', {
      visible: this.visible,
      hasSearchResults: this.hasSearchResults,
      hasLocations: this.hasLocations,
      hasDoctors: this.hasDoctors,
      hasServices: this.hasServices,
      hasNews: this.hasNews,
    });

    if (!this.visible) {
      return nothing;
    }

    return html`
      <div class="typeahead-results-container">
        ${this.renderSection(this.hasSearchResults, 'search-results')}
        ${this.renderSection(this.hasLocations, 'locations')}
        ${this.renderSection(this.hasDoctors, 'doctors')}
        ${this.renderSection(this.hasServices, 'services')}
        ${this.renderSection(this.hasNews, 'news')}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nuvance-decoupled-search-typeahead-results': NuvanceDecoupledSearchTypeaheadResults;
  }
}
